import React, { useContext, useMemo } from "react";
import compose from "compose-function";
import moment from "moment";

import TableComponent from "../../components/TableComponent";
import CreateItemButton from "../../components/CreateItemButton";

import { useColumns } from "../../hooks/useColumns";
import useUserPermissionsContext from "../../hooks/useUserPermissionsContext";

import {
  getSortOrder,
  sortColumns,
  sortModelToString,
  fetchDataWithOptionsAndFilters,
  exportToFile,
} from "../../utils/utils";

import {
  addHrefToRow,
  addProjectReferenceField,
  changeDeletedToBoolean,
  addValueGetter,
  getClient,
  readDataFromLocalStorage,
  getDefaultFilterModel,
  getInitialFilters,
  generateEntityKeyForLocalStorage,
  addFormatters,
} from "../../utils/table";

import { getDefaultSort } from "../../constants/defaultSorting";

import { hardDelete, softDelete } from "../../dao/operations";
import { exportRecords } from "../../dao/common";
import { FILTER_TYPES } from "../../dao/types";

import UserRights from "../../enums/UserRights";

import { ROUTES } from "../../enums/Routes";
import { API_ROUTES } from "../../enums/api";
import { addSearchParams } from "../../utils/dao";

import { GlobalContext } from "../../globalContext";
import useEntities from "../../hooks/useEntities";
import { useSortModel } from "../../hooks/useSortModel";

const API_ROUTE = API_ROUTES.projects;

/**
 * Here we need to transform the column data retrieved,
 * in order to make it compatible with @mui/x-data-grid-pro
 */
const transformColumns = (columns) => {

  return addFormatters(
    [
      { field: "client.client", formatter: addValueGetter("client.client")(getClient("client"))},
      { field: "client.name", formatter: addValueGetter("client.name")(getClient("name")) },
      { field: "client.number", formatter: addValueGetter("client.number")(getClient("number")) },
    ], 
    columns);
};

/**
 * Here we need to transform the row data retrieved,
 * in order to make it compatible with @mui/x-data-grid-pro
 */
const transformRows = (rows) => {
  return rows.reduce((acc, row) => {
    return [
      ...acc,
      compose(
        changeDeletedToBoolean("deleted"),
        addHrefToRow({
          field: "id",
          slug: "project",
        }),
        addProjectReferenceField
      )(row),
    ];
  }, []);
};

export default function ProjectsContainer({
  nestedEntity = false,
  parent = {},
}) {
  const { globalFilters } = useContext(GlobalContext);

  // Get cached local filter model from local storage
  // with a fallback to default field values
  const cachedLocalFilter = readDataFromLocalStorage(
    generateEntityKeyForLocalStorage(FILTER_TYPES.project, "filterModel", {
      nestedEntity,
    })
  ) ?? {
    filterModel: null,
    filterJSON: "",
  };

  // destructure fields from cachedLocalFilter
  const {
    filterModel: cachedLocalFilterModel,
    filterJSON: cachedFilterJSONString,
  } = cachedLocalFilter;

  // Dynamically calculate filters based on global filter state
  const initialFilters = useMemo(() => {
    // Get the base filter model without any additional filters
    const baseFilters = getInitialFilters({
      cache: {
        localFilterModel: cachedLocalFilterModel,
        filterJSONString: cachedFilterJSONString,
      },
      defaultFilterModel: getDefaultFilterModel("object_number").filterModel,
    });

    // Create a copy of the grid filters to modify
    const gridFilters = { ...baseFilters.grid };

    // Ensure items array exists
    if (!gridFilters.items) {
      gridFilters.items = [];
    }

    // Remove any existing object_number isEmpty filters
    gridFilters.items = gridFilters.items.filter(
      (item) =>
        !(
          item.columnField === "object_number" &&
          item.operatorValue === "isEmpty"
        )
    );

    // Add object_number isEmpty filter as last item when no global filter is applied
    if (!globalFilters?.value && !parent?.object_id) {
      gridFilters.items.push({
        columnField: "object_number",
        operatorValue: "isEmpty",
        value: "",
      });
    }

    return {
      ...baseFilters,
      grid: gridFilters,
    };
  }, [
    globalFilters?.value,
    parent?.object_id,
    cachedLocalFilterModel,
    cachedFilterJSONString,
  ]);

  const { columns } = useColumns(FILTER_TYPES.project);

  const defaultSortString = getDefaultSort(FILTER_TYPES.project);
  const { sortModel, setSortModel } = useSortModel(FILTER_TYPES.project, {
    nestedEntity,
    columns,
  });
  const sortModelString =
    sortModelToString(sortModel, columns) || defaultSortString;

  const {
    data,
    loadingState,
    fetchEntitiesData: fetchProjectsData,
    setFilterString,
    setOffset,
    limit,
    offset,
    setLimit,
    totalNumberOfRecords,
  } = useEntities("projects", {
    params: { object_ids: parent?.object_id ?? globalFilters?.value ?? null },
    initialFilters: initialFilters.apiFilterString,
    order: sortModelString,
  });

  const fetchRecordsDataWithGlobalFilters = fetchDataWithOptionsAndFilters(
    fetchProjectsData
  )({
    limit,
    offset,
    order: sortModelString,
    params: {
      object_ids: parent?.object_id ?? globalFilters?.value ?? null,
    },
  });

  const fetchExportData = async (filterString, selectedColumnsString) => {
    const abortController = new AbortController();
    const searchParams = {
      filters: filterString,
      columns: selectedColumnsString,
      object_ids: parent?.object_id || globalFilters?.value,
    };
    const response = await exportRecords(
      API_ROUTE,
      searchParams,
      abortController
    );

    exportToFile(
      `export_${FILTER_TYPES.project}_overview_${moment().format(
        "DD-MM-YYYY"
      )}.csv`,
      response
    );

    return "Export done";
  };

  const { isAllowedTo } = useUserPermissionsContext();

  const isAllowedToCreate = isAllowedTo(
    UserRights.CREATE,
    FILTER_TYPES.project
  );
  const isAllowedToDelete = isAllowedTo(
    UserRights.SOFT_DELETE,
    FILTER_TYPES.project
  );
  const isAllowedToExport = isAllowedTo(UserRights.EXPORT_CSV);
  const isAllowedToInlineEdit = isAllowedTo(UserRights.INLINE_EDIT_PROJECT);

  const tableOptions = {};
  tableOptions.sortOrder = getSortOrder(FILTER_TYPES.project);
  tableOptions.hasDelete = isAllowedToDelete;
  tableOptions.hasInlineEdit = isAllowedToInlineEdit;

  const searchParams = new URLSearchParams(
    Object.entries({
      objectId: parent?.object_id || globalFilters?.value,
    }).filter(([, value]) => value)
  ).toString();

  return (
    <>
      <TableComponent
        key={`projects-table-${!!globalFilters?.value}-${!!parent?.object_id}`}
        {...{
          columns: sortColumns(transformColumns(columns)),
          transformColumns,
          loading: loadingState,
          rows: transformRows(data),
          refreshData: fetchRecordsDataWithGlobalFilters,
          renderCreateEntityButton: () => (
            <CreateItemButton
              to={addSearchParams(ROUTES.project.create)(searchParams)}
              isDisabled={!isAllowedToCreate}
              entity={FILTER_TYPES.project}
            />
          ),
          softDeleteEntry: softDelete({
            route: API_ROUTE,
            delete: true,
          }),
          hardDeleteEntry: hardDelete({
            route: API_ROUTE,
          }),
          restoreEntry: softDelete({
            route: API_ROUTE,
            delete: false,
          }),
          tableOptions,
          setFilters: setFilterString,
          setOffset,
          offset,
          setLimit,
          sortModel,
          setSortModel,
          numberOfRecords: totalNumberOfRecords,
          isAllowedToExport,
          entity: FILTER_TYPES.project,
          nestedEntity: nestedEntity,
          initialFilterModel: initialFilters.grid,
          exportRecords: fetchExportData,
        }}
      />
    </>
  );
}
